import React, { useState, useCallback, useMemo } from 'react';
import {
  Campaign,
  CampaignDetail,
  HsmTemplate,
  HsmContact,
  HsmContactBatch,
  CampaignCreateRequest,
  CampaignAddContactsRequest
} from '../../types';
import { CampaignRestartRequest } from '../../services/hsm-campaigns.service';

// Import extracted components
import { CampaignListView } from './CampaignListView';
import { CampaignDetailView } from './CampaignDetailView';
import { CreateCampaignDialog } from './CreateCampaignDialog';
import { RestartCampaignDialog } from './RestartCampaignDialog';
import { DeleteCampaignDialog } from './DeleteCampaignDialog';
import { AddContactsDialog } from './AddContactsDialog';

export interface HsmCampaignsTabProps {
  // State
  campaigns: Campaign[];
  campaignsLoading: boolean;
  selectedCampaign: CampaignDetail | null;
  campaignDetailLoading: boolean;

  // Templates
  approvedTemplates: HsmTemplate[];
  templatesLoading: boolean;

  // Contacts for selection
  contacts: HsmContact[];
  contactsLoading: boolean;

  // Batches for selection
  batches: HsmContactBatch[];
  batchesLoading: boolean;

  // Permissions
  isAdmin: boolean;

  // Handlers
  fetchCampaigns: () => Promise<{ success: boolean; error?: string }>;
  fetchCampaignDetail: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCreateCampaign: (data: CampaignCreateRequest) => Promise<{ success: boolean; error?: string }>;
  handleStartCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handlePauseCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleResumeCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleCompleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  handleRestartCampaign: (campaignId: number, data: CampaignRestartRequest) => Promise<{ success: boolean; error?: string; contactsReset?: number }>;
  handleAddContacts: (campaignId: number, data: CampaignAddContactsRequest) => Promise<{ success: boolean; error?: string }>;
  handleRunCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string; result?: { sent: number; failed: number } }>;
  handleDeleteCampaign: (campaignId: number) => Promise<{ success: boolean; error?: string }>;
  clearSelectedCampaign: () => void;
  getTemplateVariableMapping: (templateName: string) => Promise<{ success: boolean; variableMapping?: string[] | null; error?: string }>;
}

export function HsmCampaignsTab({
  campaigns,
  campaignsLoading,
  selectedCampaign,
  campaignDetailLoading,
  approvedTemplates,
  templatesLoading,
  contacts,
  contactsLoading,
  batches,
  batchesLoading,
  isAdmin,
  fetchCampaigns,
  fetchCampaignDetail,
  handleCreateCampaign,
  handleStartCampaign,
  handlePauseCampaign,
  handleResumeCampaign,
  handleCompleteCampaign,
  handleRestartCampaign,
  handleAddContacts,
  handleRunCampaign,
  handleDeleteCampaign,
  clearSelectedCampaign,
  getTemplateVariableMapping
}: HsmCampaignsTabProps) {
  // Create campaign dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Add contacts dialog state
  const [addContactsDialogOpen, setAddContactsDialogOpen] = useState(false);
  const [addContactsLoading, setAddContactsLoading] = useState(false);
  const [contactsToAdd, setContactsToAdd] = useState<number[]>([]);
  const [batchesToAdd, setBatchesToAdd] = useState<number[]>([]);

  // Restart campaign dialog state
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [campaignToRestart, setCampaignToRestart] = useState<Campaign | CampaignDetail | null>(null);
  const [restartSuccessMessage, setRestartSuccessMessage] = useState<string | null>(null);

  // Run campaign state
  const [runningCampaignId, setRunningCampaignId] = useState<number | null>(null);
  const [runSuccessMessage, setRunSuccessMessage] = useState<string | null>(null);

  // Delete campaign state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState<string | null>(null);

  // Handle campaign actions
  const handleAction = useCallback(async (action: 'start' | 'pause' | 'resume' | 'complete', campaignId: number) => {
    let result: { success: boolean; error?: string };
    switch (action) {
      case 'start':
        result = await handleStartCampaign(campaignId);
        break;
      case 'pause':
        result = await handlePauseCampaign(campaignId);
        break;
      case 'resume':
        result = await handleResumeCampaign(campaignId);
        break;
      case 'complete':
        result = await handleCompleteCampaign(campaignId);
        break;
    }
    if (result.success) {
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        fetchCampaignDetail(campaignId);
      }
    }
  }, [handleStartCampaign, handlePauseCampaign, handleResumeCampaign, handleCompleteCampaign, fetchCampaigns, fetchCampaignDetail, selectedCampaign?.id]);

  // Handle add contacts
  const handleAddContactsSubmit = useCallback(async () => {
    if (!selectedCampaign || (contactsToAdd.length === 0 && batchesToAdd.length === 0)) return;

    setAddContactsLoading(true);
    const result = await handleAddContacts(selectedCampaign.id, {
      contactIds: contactsToAdd.length > 0 ? contactsToAdd : undefined,
      batchIds: batchesToAdd.length > 0 ? batchesToAdd : undefined
    });
    setAddContactsLoading(false);

    if (result.success) {
      setAddContactsDialogOpen(false);
      setContactsToAdd([]);
      setBatchesToAdd([]);
      fetchCampaignDetail(selectedCampaign.id);
    }
  }, [selectedCampaign, contactsToAdd, batchesToAdd, handleAddContacts, fetchCampaignDetail]);

  // Handle run campaign (with forceResend=true for testing)
  const handleRunCampaignClick = useCallback(async (campaignId: number) => {
    if (!confirm('Tem certeza que deseja executar esta campanha agora? Isso enviara mensagens para os contatos elegiveis.')) return;

    setRunningCampaignId(campaignId);
    const result = await handleRunCampaign(campaignId);
    setRunningCampaignId(null);

    if (result.success && result.result) {
      const { sent, failed } = result.result;
      setRunSuccessMessage(`Campanha executada! ${sent} mensagem${sent !== 1 ? 's' : ''} enviada${sent !== 1 ? 's' : ''}${failed > 0 ? `, ${failed} falha${failed !== 1 ? 's' : ''}` : ''}.`);
      // Clear success message after 5 seconds
      setTimeout(() => setRunSuccessMessage(null), 5000);
    }
  }, [handleRunCampaign]);

  // Open delete confirmation dialog
  const openDeleteDialog = useCallback((campaign: Campaign) => {
    setCampaignToDelete(campaign);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  }, []);

  // Handle delete campaign confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!campaignToDelete) return;

    setDeleting(true);
    setDeleteError(null);

    const result = await handleDeleteCampaign(campaignToDelete.id);
    setDeleting(false);

    if (result.success) {
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      setDeleteSuccessMessage(`Campanha "${campaignToDelete.name}" excluida com sucesso.`);
      // Clear success message after 5 seconds
      setTimeout(() => setDeleteSuccessMessage(null), 5000);
    } else {
      setDeleteError(result.error || 'Erro ao excluir campanha');
    }
  }, [campaignToDelete, handleDeleteCampaign]);

  // Reset delete dialog state
  const resetDeleteDialog = useCallback(() => {
    setCampaignToDelete(null);
    setDeleteError(null);
    setDeleteDialogOpen(false);
  }, []);

  // Open restart dialog
  const openRestartDialog = useCallback((campaign: Campaign | CampaignDetail) => {
    setCampaignToRestart(campaign);
    setRestartDialogOpen(true);
  }, []);

  // Handle restart campaign
  const handleRestartSubmit = useCallback(async (data: CampaignRestartRequest) => {
    if (!campaignToRestart) return { success: false, error: 'Campanha nao encontrada' };

    const result = await handleRestartCampaign(campaignToRestart.id, data);

    if (result.success) {
      const contactsReset = result.contactsReset ?? 0;
      setRestartSuccessMessage(
        `Campanha reiniciada com sucesso! ${contactsReset} contato${contactsReset !== 1 ? 's' : ''} ${contactsReset !== 1 ? 'foram reiniciados' : 'foi reiniciado'}.`
      );
      fetchCampaigns();
      if (selectedCampaign?.id === campaignToRestart.id) {
        fetchCampaignDetail(campaignToRestart.id);
      }
      setCampaignToRestart(null);
      // Clear success message after 5 seconds
      setTimeout(() => setRestartSuccessMessage(null), 5000);
    }

    return result;
  }, [campaignToRestart, handleRestartCampaign, fetchCampaigns, fetchCampaignDetail, selectedCampaign?.id]);

  // Get contacts not already in selected campaign
  const availableContactsForCampaign = useMemo(() => {
    if (!selectedCampaign) return contacts;
    const enrolledIds = new Set(selectedCampaign.contacts.map(c => c.contactId));
    return contacts.filter(c => !enrolledIds.has(c.id));
  }, [contacts, selectedCampaign]);

  // Options for batch selection (used in both dialogs)
  const batchOptions = useMemo(() => {
    return batches.map((batch) => ({
      value: batch.id,
      label: batch.name,
      description: `${batch.contactCount} contato${batch.contactCount !== 1 ? 's' : ''}`
    }));
  }, [batches]);

  // Options for contact selection in create campaign dialog
  const contactOptions = useMemo(() => {
    return contacts.map((contact) => ({
      value: contact.id,
      label: contact.name || contact.phoneNumber,
      description: contact.name ? contact.phoneNumber : undefined
    }));
  }, [contacts]);

  // Options for contact selection in add contacts dialog (excluding already enrolled)
  const availableContactOptions = useMemo(() => {
    return availableContactsForCampaign.map((contact) => ({
      value: contact.id,
      label: contact.name || contact.phoneNumber,
      description: contact.name ? contact.phoneNumber : undefined
    }));
  }, [availableContactsForCampaign]);

  // Render campaign list view or detail view
  if (!selectedCampaign) {
    return (
      <>
        <CampaignListView
          campaigns={campaigns}
          campaignsLoading={campaignsLoading}
          restartSuccessMessage={restartSuccessMessage}
          deleteSuccessMessage={deleteSuccessMessage}
          onCreateClick={() => setCreateDialogOpen(true)}
          onRefresh={fetchCampaigns}
          onCampaignClick={fetchCampaignDetail}
          onStartCampaign={(id) => handleAction('start', id)}
          onPauseCampaign={(id) => handleAction('pause', id)}
          onResumeCampaign={(id) => handleAction('resume', id)}
          onRestartCampaign={openRestartDialog}
          onDeleteCampaign={openDeleteDialog}
        />

        <DeleteCampaignDialog
          open={deleteDialogOpen}
          campaign={campaignToDelete}
          deleting={deleting}
          error={deleteError}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={resetDeleteDialog}
        />

        <CreateCampaignDialog
          open={createDialogOpen}
          approvedTemplates={approvedTemplates}
          templatesLoading={templatesLoading}
          batchOptions={batchOptions}
          contactOptions={contactOptions}
          batchesLoading={batchesLoading}
          contactsLoading={contactsLoading}
          onOpenChange={setCreateDialogOpen}
          onSubmit={handleCreateCampaign}
          getTemplateVariableMapping={getTemplateVariableMapping}
        />

        <RestartCampaignDialog
          open={restartDialogOpen}
          campaign={campaignToRestart}
          approvedTemplates={approvedTemplates}
          templatesLoading={templatesLoading}
          onOpenChange={(open) => {
            setRestartDialogOpen(open);
            if (!open) setCampaignToRestart(null);
          }}
          onSubmit={handleRestartSubmit}
          getTemplateVariableMapping={getTemplateVariableMapping}
        />
      </>
    );
  }

  // Render campaign detail view
  return (
    <>
      <CampaignDetailView
        campaign={selectedCampaign}
        loading={campaignDetailLoading}
        approvedTemplates={approvedTemplates}
        runningCampaignId={runningCampaignId}
        restartSuccessMessage={restartSuccessMessage}
        runSuccessMessage={runSuccessMessage}
        onBack={clearSelectedCampaign}
        onStartCampaign={() => handleAction('start', selectedCampaign.id)}
        onPauseCampaign={() => handleAction('pause', selectedCampaign.id)}
        onResumeCampaign={() => handleAction('resume', selectedCampaign.id)}
        onCompleteCampaign={() => handleAction('complete', selectedCampaign.id)}
        onRestartCampaign={() => openRestartDialog(selectedCampaign)}
        onRunCampaign={() => handleRunCampaignClick(selectedCampaign.id)}
        onAddContacts={() => {
          setContactsToAdd([]);
          setBatchesToAdd([]);
          setAddContactsDialogOpen(true);
        }}
      />

      <AddContactsDialog
        open={addContactsDialogOpen}
        loading={addContactsLoading}
        contactsToAdd={contactsToAdd}
        batchesToAdd={batchesToAdd}
        batchOptions={batchOptions}
        contactOptions={availableContactOptions}
        batchesLoading={batchesLoading}
        contactsLoading={contactsLoading}
        onOpenChange={setAddContactsDialogOpen}
        onContactsChange={setContactsToAdd}
        onBatchesChange={setBatchesToAdd}
        onSubmit={handleAddContactsSubmit}
        emptyContactsMessage={
          availableContactsForCampaign.length === 0
            ? 'Todos os contatos ja estao inscritos nesta campanha.'
            : 'Nenhum contato encontrado.'
        }
      />

      <RestartCampaignDialog
        open={restartDialogOpen}
        campaign={campaignToRestart}
        approvedTemplates={approvedTemplates}
        templatesLoading={templatesLoading}
        onOpenChange={(open) => {
          setRestartDialogOpen(open);
          if (!open) setCampaignToRestart(null);
        }}
        onSubmit={handleRestartSubmit}
        getTemplateVariableMapping={getTemplateVariableMapping}
      />
    </>
  );
}
